import React, { useMemo, useState } from 'react';
import { FetchResult } from '@apollo/client';
import { faBookmark } from '@fortawesome/free-solid-svg-icons/faBookmark';
import { faArchive } from '@fortawesome/free-solid-svg-icons/faArchive';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';

import {
  MainNavigationCurrentUserDocument,
  MainNavigationCurrentUserQuery,
  UpdateReadingListItemMutationVariables,
  UpdateReadingListItemMutation,
  useCreateReadingListItemMutation,
  useMainNavigationCurrentUserQuery,
  useUpdateReadingListItemMutation
} from 'interfaces/graphql-typings';

import styles from './ideaReadingListButtonStyles.scss';

const IdeaReadingListButton: React.FC<{
  button?: boolean;
  buttonSize?: BUTTON_SIZES;
  ideaId: string;
}> = ({ button, buttonSize = BUTTON_SIZES.MEDIUM, ideaId }): React.ReactElement => {
  const [submitted, setSubmitted] = useState(false);

  const { data } = useMainNavigationCurrentUserQuery();

  const readingListItem = useMemo(
    () =>
      data?.currentUser?.readingListItems?.edges?.find(edge => edge?.node?.ideaId === ideaId)?.node,
    [data?.currentUser?.readingListItems?.edges, ideaId]
  );

  const resetSubmit = (): void => setSubmitted(false);

  const [
    handleAddToReadingList,
    { loading: addToReadingListLoading }
  ] = useCreateReadingListItemMutation({
    variables: { ideaId },
    update: (cache, { data }) => {
      const newItem = data?.createOrUpdateReadingListItem?.readingListItem;
      const currentUserId = data?.createOrUpdateReadingListItem?.currentUser?.id;

      if (!newItem || !currentUserId) return;

      const cachedQuery = cache.readQuery<MainNavigationCurrentUserQuery>({
        query: MainNavigationCurrentUserDocument
      });
      if (!cachedQuery?.currentUser?.readingListItems) return;

      cache.writeQuery<MainNavigationCurrentUserQuery>({
        query: MainNavigationCurrentUserDocument,
        data: {
          currentUser: {
            ...cachedQuery.currentUser,
            readingListItems: {
              edges: [...(cachedQuery.currentUser.readingListItems.edges || []), { node: newItem }]
            }
          }
        }
      });
    }
  });

  const [
    handleUpdateReadingList,
    { loading: updateReadingListLoading }
  ] = useUpdateReadingListItemMutation({
    update: (cache, { data }) => {
      const updatedItem = data?.createOrUpdateReadingListItem?.readingListItem;
      const currentUserId = data?.createOrUpdateReadingListItem?.currentUser?.id;

      if (!updatedItem || !currentUserId) return;

      const cachedQuery = cache.readQuery<MainNavigationCurrentUserQuery>({
        query: MainNavigationCurrentUserDocument
      });
      if (!cachedQuery?.currentUser?.readingListItems) return;

      cache.writeQuery<MainNavigationCurrentUserQuery>({
        query: MainNavigationCurrentUserDocument,
        data: {
          currentUser: {
            ...cachedQuery.currentUser,
            readingListItems: {
              edges: [
                ...(cachedQuery.currentUser.readingListItems.edges?.filter(
                  edge => edge?.node?.id !== updatedItem.id
                ) || []),
                { node: updatedItem }
              ]
            }
          }
        }
      });
    }
  });

  const handleReadLater = (): void => {
    handleAddToReadingList().then(() => setSubmitted(true));
  };

  const handleUpdate = ({
    readingListItemId,
    archived
  }: UpdateReadingListItemMutationVariables): Promise<FetchResult<UpdateReadingListItemMutation>> =>
    handleUpdateReadingList({ variables: { readingListItemId, archived } });

  const toggleArchiveIdea = (archived: boolean): void => {
    readingListItem &&
      handleUpdate({ readingListItemId: readingListItem?.id, archived }).then(() =>
        setSubmitted(true)
      );
  };

  const handleSubmit = (): void => {
    if (readingListItem) {
      submitted ? resetSubmit() : toggleArchiveIdea(!readingListItem.archived);
    } else {
      return handleReadLater();
    }
  };

  let buttonText,
    buttonIcon = null,
    buttonAnalyticsTag = '';

  if (readingListItem) {
    if (readingListItem?.archived) {
      submitted ? (buttonText = 'Idea Archived') : (buttonText = 'Move to Reading List');
      submitted ? (buttonIcon = null) : (buttonIcon = faBookmark);
    } else if (!readingListItem?.archived) {
      submitted ? (buttonText = 'Saved') : (buttonText = 'Archive Idea');
      submitted ? (buttonIcon = null) : (buttonIcon = faArchive);
    }
  } else {
    buttonText = 'Read Later';
    buttonIcon = faBookmark;
    buttonAnalyticsTag = 'read-later';
  }

  return (
    <Button
      analyticsTag={buttonAnalyticsTag}
      faIcon={submitted ? faCheck : buttonIcon}
      iconStyle={submitted ? styles.checkIcon : null}
      isLoading={addToReadingListLoading || updateReadingListLoading}
      onClick={handleSubmit}
      size={buttonSize}
      text={buttonText}
      type={button ? BUTTON_TYPES.SECONDARY : BUTTON_TYPES.LINK}
    />
  );
};

export default IdeaReadingListButton;
